import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import PageWrapper from "@/components/layout/page-wrapper";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useGetFeaturePlan } from "@/api/plan/get-feature-plan";
import { Input } from "@/components/ui/input";
import { cn, formatDate } from "@/lib/utils";
import { useUpdatePlanFeature } from "@/api/plan/update-plan-feature";
import { Skeleton } from "@/components/ui/skeleton";
import FullscreenLoading from "@/components/layout/fullscreen-loading";

type Feature = {
    id: number;
    feature_id: number;
    value: string;
    feature_name: string;
    feature_type: string;
};

const PackageDetailsPage = () => {
    const [isEdit, setIsEdit] = useState(-1);
    const [features, setFeatures] = useState<Feature[]>([]);

    const { id } = useParams();

    const { data: plan, isLoading: planLoading, error: planError } = useGetFeaturePlan(id ?? "");
    const { mutate: updatePlanFeature, isPending: updatePlanFeatureLoading, error: updatePlanFeatureError } = useUpdatePlanFeature();

    useEffect(() => {
        if (plan?.data?.features) {
            setFeatures(plan?.data?.features);
        }
    }, [plan]);

    const handleValueChange = (value: string, index: number, type: string) => {
        if (updatePlanFeatureLoading) return;
        if (updatePlanFeatureError) return;
        if (type === "boolean") {
            updatePlanFeature({
                plan_feature_id: `${features[index].id}`,
                plan_id: `${id}`,
                feature_id: `${features[index].feature_id}`,
                value,
            });
        }

        const newFeatures = [...features];
        newFeatures[index].value = value;
        setFeatures(newFeatures);
    };

    if (planLoading) return <FullscreenLoading />;
    if (planError) return <p>Failed to load features.</p>;

    return (
        <PageWrapper pageTitle="Package Details" currentPage="Package Details">
            <div className="mx-auto py-8">
                <Card className="shadow-none border p-4">
                    <h1 className="text-2xl font-bold">Package Details</h1>
                    <Separator className="my-4" />
                    <CardContent className="space-y-6 p-6">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <div>
                                <h2 className="text-xl font-semibold">{plan?.data?.plan_name}</h2>
                                <Badge className={cn(plan?.data?.is_active ? "bg-green-200 text-green-800" : "bg-red-200 text-red-800", "mt-1")}>
                                    {plan?.data?.is_active ? "Active" : "Inactive"}
                                </Badge>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Created at</p>
                                <p className="font-medium">{plan?.data?.plan_created_at ? formatDate(new Date(plan.data.plan_created_at)) : "N/A"}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Price</p>
                                <p className="text-lg font-medium">${plan?.data?.plan_price}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Duration</p>
                                <p className="text-lg font-medium capitalize">{plan?.data?.plans_interval || "N/A"}</p>
                            </div>
                            <div className="col-span-4 mt-4">
                                <p className="text-sm text-muted-foreground">Description</p>
                                <p className="text-md">{plan?.data?.plan_description}</p>
                            </div>
                        </div>

                        <div>
                            <p className="text-sm text-muted-foreground mb-2">Features</p>
                            <div className="grid grid-cols-3 items-center gap-4">
                                {features?.map((feature: Feature) =>
                                    updatePlanFeatureLoading ? (
                                        <>
                                            <Skeleton className="h-16" />
                                        </>
                                    ) : (
                                        <>
                                            <div key={feature.feature_id} className="flex items-center justify-between mb-2 border p-4 rounded-lg">
                                                <p className="text-sm font-medium">{feature.feature_name}</p>
                                                {feature.feature_type === "boolean" ? (
                                                    <Switch
                                                        checked={feature.value === "true"}
                                                        onCheckedChange={(checked) =>
                                                            handleValueChange(checked ? "true" : "false", features.indexOf(feature), "boolean")
                                                        }
                                                    />
                                                ) : (
                                                    <div className="flex items-center">
                                                        <Input
                                                            className="w-20"
                                                            value={feature.value}
                                                            onChange={(e) => {
                                                                setIsEdit(features.indexOf(feature));
                                                                handleValueChange(e.target.value, features.indexOf(feature), "string");
                                                            }}
                                                        />
                                                        {isEdit === features.indexOf(feature) && (
                                                            <Button
                                                                className="ml-2"
                                                                onClick={() => {
                                                                    updatePlanFeature({
                                                                        plan_feature_id: `${feature.id}`,
                                                                        plan_id: `${id}`,
                                                                        feature_id: `${feature.feature_id}`,
                                                                        value: features[features.indexOf(feature)].value,
                                                                    });
                                                                    setIsEdit(-1);
                                                                }}
                                                            >
                                                                Save
                                                            </Button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            {/* <Separator className="my-4" /> */}
                                        </>
                                    )
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </PageWrapper>
    );
};

export default PackageDetailsPage;
